// The side panel's form: from a stored day to the form, and from the form to
// what create/update store (every field attendanceColumns keeps).
import { kindOf } from './weekModel';

const num = (v, fallback = '') => {
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : fallback;
};
const hhmm = (t) => (t ? String(t).slice(0, 5) : '');

/** Hours between two HH:MM times minus the break; out earlier than in = next day. */
export const hoursFromTimes = (checkIn, checkOut, breakMinutes) => {
  if (!checkIn || !checkOut) return null;
  const [ih, im] = checkIn.split(':').map(Number);
  const [oh, om] = checkOut.split(':').map(Number);
  let minutes = (oh * 60 + om) - (ih * 60 + im);
  if (minutes < 0) minutes += 24 * 60;
  minutes -= parseInt(breakMinutes, 10) || 0;
  return Math.max(0, Math.round((minutes / 60) * 100) / 100);
};

/**
 * Hours a day counts when it has no times, by status: the same rule as
 * "Mark as...". Hourly: present and paid leave a standard day, others 0.
 * Monthly (payroll frozen): a standard day whatever the status.
 */
export const defaultHours = (status, leaveIsPaid, employee, standardHours) => {
  if (employee.payment_type !== 'hourly') return standardHours;
  if (status === 'present' || (status === 'leave' && leaveIsPaid)) return standardHours;
  return 0;
};

const STATUS_OF_KIND = { present: 'present', night: 'present', open: 'present', absent: 'absent', sick: 'sick', leave: 'leave', unpaid: 'leave' };

export const formFromRecord = (record, employee, standardHours) => {
  const monthly = employee.payment_type !== 'hourly';
  if (!record) {
    return {
      status: 'present',
      leave_type_id: '',
      check_in_time: '',
      check_out_time: '',
      break_minutes: '0',
      hours_worked: String(standardHours),
      has_overtime: false,
      overtime: '0',
      is_late: false,
      late_minutes: '0',
      late_deduction_percent: String(num(employee.late_deduction_percent, 10)),
      absent_reason: '',
      sick_reason: '',
      company_holiday: false,
      notes: '',
    };
  }
  const overtimeMinutes = parseInt(record.overtime_minutes, 10) || 0;
  const overtimeHours = num(record.overtime_hours, 0);
  return {
    status: STATUS_OF_KIND[kindOf(record)] || 'present',
    leave_type_id: record.leave_type_id != null ? String(record.leave_type_id) : '',
    check_in_time: hhmm(record.check_in_time),
    check_out_time: hhmm(record.check_out_time),
    break_minutes: String(parseInt(record.break_minutes, 10) || 0),
    hours_worked: record.hours_worked != null ? String(num(record.hours_worked, 0)) : '',
    has_overtime: overtimeMinutes > 0 || overtimeHours > 0,
    // Monthly overtime is paid in minutes, hourly in hours.
    overtime: String(monthly ? overtimeMinutes + Math.round(overtimeHours * 60) : Math.round((overtimeHours + overtimeMinutes / 60) * 100) / 100),
    is_late: Boolean(record.is_late) && (parseInt(record.late_minutes, 10) || 0) > 0,
    late_minutes: String(parseInt(record.late_minutes, 10) || 0),
    late_deduction_percent: String(num(record.late_deduction_percent, num(employee.late_deduction_percent, 10))),
    absent_reason: record.absent_reason || '',
    sick_reason: record.sick_reason || '',
    company_holiday: Boolean(record.is_holiday),
    notes: record.notes || '',
  };
};

/**
 * What create/update receive. Fields of other statuses are cleared. The
 * day's sick-pay and absence-deduction percentages are not sent (payroll
 * does not use them): update keeps whatever is stored.
 */
export const payloadFromForm = (form, employee, day) => {
  const monthly = employee.payment_type !== 'hourly';
  const present = form.status === 'present';
  const overtime = form.has_overtime ? num(form.overtime, 0) : 0;
  const payload = {
    status: form.status,
    leave_type_id: form.status === 'leave' && form.leave_type_id ? Number(form.leave_type_id) : null,
    check_in_time: present && form.check_in_time ? form.check_in_time : null,
    check_out_time: present && form.check_out_time ? form.check_out_time : null,
    break_minutes: present ? parseInt(form.break_minutes, 10) || 0 : 0,
    hours_worked: num(form.hours_worked, 0),
    overtime_hours: monthly ? 0 : overtime,
    overtime_minutes: monthly ? Math.round(overtime) : 0,
    late_minutes: present && form.is_late ? parseInt(form.late_minutes, 10) || 0 : 0,
    late_deduction_percent: num(form.late_deduction_percent, 0),
    is_absent: form.status === 'absent',
    absent_reason: form.status === 'absent' ? form.absent_reason : '',
    sick_reason: form.status === 'sick' ? form.sick_reason : '',
    notes: form.notes,
  };
  // A public holiday comes from the calendar; only a company holiday is sent.
  if (!day.is_holiday) payload.is_holiday = Boolean(form.company_holiday);
  return payload;
};
